import { useState, useEffect } from "react";
import { onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import Icon from "../ui/Icon";
import StatusProgress from "../ui/StatusProgress";
import AssignTaskButton from "../workflow/AssignTaskButton";
import { BRAND, THEMES, STAFF, STAFF_COLORS, STATUS_FLOW, STATUS_META } from "../../config/constants";
import { db } from "../../firebase";
import useMobile from "../../hooks/useMobile";
import { sp, staffAvatar } from "../../utils/styles";

export default function BusinessFilingView({ biz, ownerClient, onClose, theme, allClients, currentUser="" }) {
  const t = THEMES[theme];
  const mobile = useMobile();
  const [bizData, setBizData] = useState(biz);
  const [saving, setSaving] = useState(false);

  // Live updates from Firestore
  useEffect(() => {
    if (!biz?.id) return;
    const unsub = onSnapshot(doc(db, "businesses", biz.id), d => {
      if (d.exists()) setBizData({ id: d.id, ...d.data() });
    });
    return unsub;
  }, [biz?.id]);

  const handleUpdate = async (updates) => {
    if (!bizData?.id) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "businesses", bizData.id), { ...updates, updatedAt: serverTimestamp() });
    } catch(e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const status = bizData?.status || "Not Started";
  const assignedTo = bizData?.assignedTo || ownerClient?.assignedTo || "Bibek";

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", padding:"4px 0", marginTop:4, display:"flex", alignItems:"center" }}>
            <Icon name="arrow" size={18} color={t.textSub}/>
          </button>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:t.text, marginBottom:6 }}>
              {bizData?.legalName || "Business"}
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600, background:theme==="dark"?"#0e2818":"#edfaf0", color:BRAND.green, border:`1px solid ${BRAND.green}30` }}>
                <Icon name="building" size={10} color={BRAND.green}/>{bizData?.entityType||"LLC"}
              </span>
              <span style={sp(status, theme)}>{status}</span>
              <span style={{ fontSize:11.5, color:t.textMuted }}>Tax Year {bizData?.taxYear || ownerClient?.taxYear}</span>
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>{staffAvatar(assignedTo, 20)}<span style={{ fontSize:11.5, color:t.textSub, fontWeight:600 }}>{assignedTo}</span></div>
            </div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <AssignTaskButton clientId={ownerClient?.id} clientName={ownerClient?.name} bizId={bizData?.id} bizName={bizData?.legalName} currentUser={currentUser} theme={theme}/>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", padding:4 }}>
            <Icon name="x" size={18} color={t.textSub}/>
          </button>
        </div>
      </div>

      {/* Owner link */}
      <div style={{ background:t.bg, border:`1px solid ${BRAND.blue}30`, borderRadius:10, padding:"10px 16px", marginBottom:14, display:"flex", alignItems:"center", gap:10 }}>
        <Icon name="user" size={14} color={BRAND.blue}/>
        <span style={{ fontSize:12.5, color:t.textSub }}>Owner: </span>
        <span style={{ fontSize:12.5, fontWeight:700, color:BRAND.blue }}>{ownerClient?.name}</span>
        {ownerClient?.email && <span style={{ fontSize:11.5, color:t.textMuted }}>· {ownerClient.email}</span>}
      </div>

      {/* Status pipeline */}
      <div style={{ background:t.bg, borderRadius:12, padding:"12px 16px", marginBottom:12, border:`1px solid ${t.border}` }}>
        <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>Tax Process</div>
        <StatusProgress status={status} theme={theme}/>
        <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:10 }}>
          {STATUS_FLOW.map(s => {
            const m = STATUS_META[s];
            const active = status === s;
            return (
              <button key={s} onClick={() => handleUpdate({ status: s })}
                style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600, cursor:"pointer", border:`1px solid ${active?m.border+"60":t.border}`, background:active?m.bg(theme):t.bg, color:active?m.text:t.textMuted, fontFamily:"'Outfit',sans-serif" }}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Assigned To */}
      <div style={{ background:t.bg, borderRadius:10, padding:"10px 14px", marginBottom:12, border:`1px solid ${t.border}`, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em" }}>Assigned To</div>
        <div style={{ display:"flex", gap:8 }}>
          {STAFF.map(s => (
            <button key={s} onClick={() => handleUpdate({ assignedTo: s })}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:20, border:`1px solid ${assignedTo===s?STAFF_COLORS[s]+"60":t.border}`, background:assignedTo===s?STAFF_COLORS[s]+"18":"transparent", cursor:"pointer", fontFamily:"'Outfit',sans-serif" }}>
              {staffAvatar(s, 18)}
              <span style={{ fontSize:11.5, fontWeight:600, color:assignedTo===s?STAFF_COLORS[s]:t.textSub }}>{s}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Business details */}
      <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:14, padding:20 }}>
        <div style={{ fontSize:13, fontWeight:700, color:t.text, marginBottom:14 }}>Business Details</div>
        <div style={{ display:"grid", gridTemplateColumns:mobile?"1fr 1fr":"repeat(3,1fr)", gap:14 }}>
          {[
            { l:"EIN",           v:bizData?.ein },
            { l:"Industry",      v:bizData?.industry },
            { l:"Year Founded",  v:bizData?.yearFounded },
            { l:"City",          v:bizData?.city },
            { l:"State",         v:bizData?.state },
            { l:"ZIP",           v:bizData?.zip },
          ].map(f => (
            <div key={f.l}>
              <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:3 }}>{f.l}</div>
              <div style={{ fontSize:12.5, color:t.text, fontWeight:500 }}>{f.v||"—"}</div>
            </div>
          ))}
          {bizData?.address && (
            <div style={{ gridColumn:"span 3" }}>
              <div style={{ fontSize:10, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:3 }}>Address</div>
              <div style={{ fontSize:12.5, color:t.text }}>{[bizData.address, bizData.city, bizData.state, bizData.zip].filter(Boolean).join(", ")}</div>
            </div>
          )}
        </div>
        {bizData?.isBookkeeping && (
          <div style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:5, background:BRAND.green+"15", border:`1px solid ${BRAND.green}40`, borderRadius:12, padding:"3px 10px", fontSize:11, color:BRAND.green, fontWeight:700 }}>
            📒 Bookkeeping Client
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Businesses Tab ──────────────────────────────────────────────────────────
